<script setup lang="ts">
import { PropType, computed, markRaw, reactive, ref, onMounted } from "vue";
import ConfigureBenchmarkDiagramTab from "../configure-tabs/ConfigureBenchmarkDiagramTab.vue";
import { Participant, Project, StepNavItem, Trainer, TranslatedProduct } from "../../../types/PersologOnlineTypes";
import { useI18n } from "vue-i18n";
import ConfigureProjectTab from "../configure-tabs/ConfigureProjectTab.vue";
import router from "../../../router";
import StepNavigation from "../../common/StepNavigation.vue";
import ConfigureBenchmarkPreviewTab from "../configure-tabs/ConfigureBenchmarkPreviewTab.vue";
import { ProfileContractorAPI } from "../../../api";
import ConfigureParticipantsTab from "../configure-tabs/ConfigureParticipantsTab.vue";
import { BENCHMARK_BASIC } from "../../../utils/ProjectUtils";
import ConfigureDefaultProductTab from "../configure-tabs/ConfigureDefaultProductTab.vue";

const { t } = useI18n();

const props = defineProps({
    product: {
        type: Object as PropType<TranslatedProduct>,
        default: null,
    },
    products: {
        type: Array as PropType<TranslatedProduct[]>,
        default: () => [],
    },
    trainer: {
        type: Object as PropType<Trainer>,
        required: true,
    },
    project: {
        type: Object as PropType<Project | undefined>,
        required: true,
    },
    importProject: {
        type: Object as PropType<Project | null>,
        default: null,
    },
    importGroupChartId: {
        type: Number as PropType<number | null>,
        default: null,
    },
});

const participants = ref<Participant[]>();

const emit = defineEmits(["set:project", "update:visible"]);
const dominantRange = ref([12.5, 87.5]);
const initiativeRange = ref([12.5, 87.5]);
const steadyRange = ref([12.5, 87.5]);
const cautiousRange = ref([12.5, 87.5]);

/**
 * All steps for the job matching dialog
 */
const stepItemsJobMatchingDialog: StepNavItem[] = reactive([
    {
        label: computed(() => t("project-configuration.tabs.configureProject")),
        icon: "pi pi-wrench",
        disabled: false,
        component: markRaw(ConfigureProjectTab),
        props: {
            products: computed(() => props.products),
            product: props.product,
            trainer: computed(() => props.trainer),
            focus: props.importProject?.focus,
            project: props.project,
            productEditable: router.currentRoute.value.path == "/projects",
        },
        events: {
            project: setProject,
        },
    },
    {
        label: t("project-configuration.tabs.configureBenchmarkDiagram"),
        icon: "pi pi-chart-bar",
        disabled: false,
        component: markRaw(ConfigureBenchmarkDiagramTab),
        props: {
            product: props.product,
            dominantRange: dominantRange,
            initiativeRange: initiativeRange,
            steadyRange: steadyRange,
            cautiousRange: cautiousRange,
            language: computed(() => props.project?.language ?? "en"),
        },
        events: {
            "update:range-values": updateRangeValues,
        },
    },
    {
        label: t("project-configuration.tabs.configureDefaultProduct"),
        icon: "pi pi-cog",
        disabled: false,
        component: markRaw(ConfigureDefaultProductTab),
        props: {
            project: computed(() => props.project),
            trainer: computed(() => props.trainer),
        },
        events: {
            "update:default-product": setProject,
        },
    },
    {
        label: computed(() => t("project-configuration.tabs.preview")),
        icon: "pi pi-check",
        disabled: false,
        component: markRaw(ConfigureBenchmarkPreviewTab),
        props: {
            project: computed(() => props.project),
            participants: participants,
            trainer: props.trainer,
            dominantRange: dominantRange,
            initiativeRange: initiativeRange,
            steadyRange: steadyRange,
            cautiousRange: cautiousRange,
        },
        events: {
            "update:visible": handleVisibilityUpdate,
        },
    },
]);

/**
 * Add the participants tab to the job matching dialog if the feature flag is enabled
 */
if (props.product?.productNo !== BENCHMARK_BASIC) {
    stepItemsJobMatchingDialog.splice(
        -1,
        0,
        reactive({
            label: computed(() => t("project-configuration.tabs.addJobCandidates")),
            icon: "pi pi-users",
            disabled: false,
            component: markRaw(ConfigureParticipantsTab),
            props: {
                id: props.project?.id,
                project: computed(() => props.project),
                participants: participants,
            },
            events: {
                "update:participants": setParticipants,
            },
        }),
    );
}

function handleVisibilityUpdate(project: Project) {
    emit("update:visible", project);
}

function setProject(project: Project) {
    emit("set:project", project);
}

function setParticipants(passedParticipants: Participant[]) {
    participants.value = passedParticipants;
}

function updateRangeValues(rangeValues: Array<number[]>) {
    dominantRange.value = rangeValues[0];
    initiativeRange.value = rangeValues[1];
    steadyRange.value = rangeValues[2];
    cautiousRange.value = rangeValues[3];
}
onMounted(async () => {
    const importId = props.importGroupChartId ?? props.importProject?.id;
    if (importId == null) return;
    try {
        const directInput = await ProfileContractorAPI.getLevelsForGroupCalculation(importId);
        const competencies = directInput.input.dimensions[0].competencies;
        const range = (name: string) => competencies.find((c) => c.name === name)?.values ?? [12.5, 87.5];
        dominantRange.value = range("D");
        initiativeRange.value = range("I");
        steadyRange.value = range("S");
        cautiousRange.value = range("G");
    } catch (error) {
        // Degrade gracefully to default ranges so the benchmark setup still opens.
        console.error("Error importing group chart levels into benchmark setup:", error);
    }
});
</script>

<template>
    <StepNavigation
        :steps="stepItemsJobMatchingDialog"
        :back-label="t('buttons.back')"
        :next-label="t('buttons.next')"
        :final-label="t('buttons.save')"
    />
</template>
